// ─────────────────────────────────────────────────────────────────
// Sentry — frontend error tracking. Silently no-ops if
// REACT_APP_SENTRY_DSN is missing (local dev / CI), so importing
// this module is always safe.
// ─────────────────────────────────────────────────────────────────
import * as SentrySDK from "@sentry/react";

// Re-bind under a normal const so `export { Sentry }` is a value
// export (webpack-friendly), not a namespace re-export (which CRA's
// webpack 5 + acorn config rejects with "was not found").
const Sentry = SentrySDK;

const DSN = process.env.REACT_APP_SENTRY_DSN;
const ENV = process.env.REACT_APP_SENTRY_ENVIRONMENT || "production";
const RELEASE = process.env.REACT_APP_VERSION || undefined;

if (DSN) {
  try {
    Sentry.init({
      dsn: DSN,
      environment: ENV,
      release: RELEASE,
      // Conservative defaults — sample 10% of perf transactions to
      // stay under free-tier quota. Crank up later if needed.
      tracesSampleRate: 0.1,
      // Don't ship PII (we handle PHI; safety first). Sentry's
      // default replay-on-error has session-side masking but we're
      // not enabling Replay at all in v1 to be cautious.
      sendDefaultPii: false,
      beforeSend(event) {
        // Drop noisy errors that don't represent real bugs:
        const msg = event?.message || event?.exception?.values?.[0]?.value || "";
        if (typeof msg === "string") {
          if (msg.includes("setSinkId")) return null;        // Twilio audio quirk
          if (msg.includes("User gesture")) return null;     // browser autoplay
          if (msg.includes("ResizeObserver loop")) return null; // common harmless warning
        }
        return event;
      },
      // Ignore expected aborts (route changes, fast clicks)
      ignoreErrors: [
        "AbortError",
        "Network request failed",
        "Failed to fetch",
      ],
    });
    // eslint-disable-next-line no-console
    console.info("[sentry] initialized", ENV);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[sentry] init failed", err);
  }
}

/**
 * Tag the current Sentry scope with the logged-in user. Call from
 * AuthContext after login. Safe to call with null/undefined to clear
 * the tag on logout. No-ops when Sentry isn't configured.
 */
export function identifySentryUser(userInfo) {
  if (!DSN) return;
  try {
    if (!userInfo) {
      Sentry.setUser(null);
      return;
    }
    const { id, email, role } = userInfo;
    Sentry.setUser(id ? { id, email, role } : null);
  } catch { /* noop */ }
}

export { Sentry };
